"use client";
import type { Instruction } from "@solana/kit";
import {
  getAssertSysvarClockInstruction,
  IntegerOperator,
} from "lighthouse-sdk";

const DEFAULT_CLOCK_ASSERTION_WINDOW_SECONDS = 60;

const hasProperty = <TKey extends string>(
  value: unknown,
  key: TKey
): value is Record<TKey, unknown> =>
  value !== null && typeof value === "object" && key in value;

const isInstruction = (value: unknown): value is Instruction => {
  if (!hasProperty(value, "programAddress")) return false;
  if (typeof value.programAddress !== "string") return false;

  if (
    hasProperty(value, "accounts") &&
    value.accounts !== undefined &&
    !Array.isArray(value.accounts)
  ) {
    return false;
  }

  if (
    hasProperty(value, "data") &&
    value.data !== undefined &&
    !(value.data instanceof Uint8Array)
  ) {
    return false;
  }

  return true;
};

const createClockAssertion = (): Instruction => {
  const instruction: unknown = getAssertSysvarClockInstruction({
    assertion: {
      __kind: "UnixTimestamp",
      value:
        BigInt(Math.floor(Date.now() / 1000)) +
        BigInt(DEFAULT_CLOCK_ASSERTION_WINDOW_SECONDS),
      operator: IntegerOperator.LessThan,
    },
  });

  if (!isInstruction(instruction)) {
    throw new Error("Invalid Lighthouse clock assertion instruction");
  }

  return instruction;
};

export const addLighthouseClockAssertion = (
  instructions: readonly Instruction[]
): Instruction[] => [...instructions, createClockAssertion()];
